// ORACLE compass "projects" layer — real data. A project is a `designs` row
// with a non-null sector; its 5 stage-fill orbs come from the 5 MIDDLE
// forecast_steps (type === 'step'), checked === true = complete. Mirrors the
// web `isProjectLinkedTask` sync-list rule (src/lib/flo.ts ~line 611) for
// callers that need to attribute tasks to a project. Also loads this week's
// scheduled tasks and links each to a project (sync rule) or, failing that,
// falls back to a sector via task_lists.olmps_sector or group-title inference
// (same heuristic as useTodayData.ts's nameMatchesSector) — see
// CompassView.tsx's "strings" overlay, the consumer of `taskLinks`.
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { SECTORS, SECTOR_LABELS, type Sector } from '../domain/olmps';
import { parseScheduled } from '../lib/autoSchedule';
import { computeProjectDrift, type DriftResult, type DriftStageInput } from './tkneDrift';
import { effectiveStatus, type StepStatus } from './stepStatus';

/** Per-sector drift rules from user_settings.olmps_drift_rules. */
export type DriftRules = Partial<Record<Sector, { cycleDays?: number }>>;

interface RawForecastStep {
  id?: string;
  type?: string;
  title?: string;
  checked?: boolean;
  date?: string;
  driftOverride?: number | null;
}

/** One `designs.basic_stages[i].substeps[j]` row. Sub-steps have no status
 *  chip — only the boolean `checked` — so they read Completed or Pending. */
interface RawSubstep {
  id?: string;
  text?: string;
  checked?: boolean;
  dueDate?: string;
}

/** One `designs.basic_stages[i]` row — an ORDER "Directions" (D-level) step. */
interface RawDirectionStep {
  id?: string;
  text?: string;
  checked?: boolean;
  status?: string;
  substeps?: unknown;
  dueDate?: string;
}

interface RawDesignRow {
  id: string;
  project_title: string | null;
  sector: string | null;
  is_active: boolean | null;
  forecast_steps: unknown;
  basic_stages: unknown;
  drift_gap_days: number | null;
  end_date: string | null;
  sync_list_id: string | null;
  sync_category_list_id: string | null;
  sync_category_name: string | null;
  updated_at: string | null;
  design_group_id: string | null;
}

/** A Directions sub-step, ready to draw as an orb hanging under its step. */
export interface OracleSubstep {
  id: string;
  text: string;
  checked: boolean;
  /** Due date (yyyy-MM-dd) set in TKNE, if any. */
  dueDate: string | null;
}

/** A Directions (ORDER "D" level) step — ASTRA's TKNE orbs are these, one orb
 *  per step, with its sub-steps hanging vertically below it. */
export interface OracleStep {
  id: string;
  text: string;
  status: StepStatus;
  substeps: OracleSubstep[];
  /** Due date (yyyy-MM-dd) set in TKNE, if any. */
  dueDate: string | null;
}

export interface OracleProject {
  id: string;
  name: string;
  sector: Sector;
  /** The 5 middle forecast steps' checked state, in progression order
   *  (stage 1 first). Always length 5, padded with `false` if a design has
   *  fewer than 5 middle steps. */
  stages: boolean[];
  /** 1-based index of the current stage — highest-index checked + 1, same
   *  logic as TkneTool.tsx's statusLabel/getCurrentMiddleStepIndex. */
  currentStage: number;
  /** Per-stage drift (segments 0..8 + score 0..4), in progression order,
   *  after the Step→Endeavour cascade. Length 5. See tkneDrift.ts. */
  drift: DriftResult[];
  /** The project's ORDER "Directions" steps (designs.basic_stages), in order.
   *  Arbitrary length — NOT the fixed five forecast stages above. This is what
   *  ASTRA's TKNE layer draws. */
  steps: OracleStep[];
  /** designs.end_date — the project's overall deadline, if set. */
  endDate: string | null;
  syncListId: string | null;
  syncCategoryListId: string | null;
  syncCategoryName: string | null;
  /** designs.updated_at — when a finished project last changed (≈ when it finished). */
  updatedAt: string | null;
}

interface RawTaskRow {
  id: string;
  title: string | null;
  scheduled: string | null;
  done: boolean | null;
  list_id: string | null;
  category: string | null;
  group_id: string | null;
}

interface RawTaskListRow {
  id: string;
  olmps_sector: string | null;
}

interface RawGroupRow {
  id: string;
  title?: string;
  isActive?: boolean;
  parentId?: string | null;
}

/** A scheduled task, resolved against the loaded projects/lists/groups to a
 *  project (sync rule) or a sector fallback — see file header. */
export interface OracleTaskLink {
  taskId: string;
  title: string;
  start: Date;
  done: boolean;
  projectId: string | null;
  sector: Sector | null;
}

function isSector(v: string | null): v is Sector {
  return !!v && (SECTORS as string[]).includes(v);
}

// Same case-insensitive "contains" match useTodayData.ts's nameMatchesSector
// uses to line up group titles against SECTOR_LABELS.
function nameMatchesSector(name: string, sectorLabel: string): boolean {
  const a = name.trim().toLowerCase();
  const b = sectorLabel.trim().toLowerCase();
  if (!a || !b) return false;
  return a.includes(b) || b.includes(a);
}

/** Start (Monday 00:00) and end (next Monday 00:00, exclusive) of the
 *  current week containing `base`. Week runs Mon–Sun. */
function currentWeekRange(base: Date): { start: Date; end: Date } {
  const d = new Date(base);
  d.setHours(0, 0, 0, 0);
  const dow = d.getDay(); // 0=Sun..6=Sat
  const diffToMonday = dow === 0 ? -6 : 1 - dow;
  const start = new Date(d);
  start.setDate(d.getDate() + diffToMonday);
  const end = new Date(start);
  end.setDate(start.getDate() + 7);
  return { start, end };
}

function asForecastSteps(raw: unknown): RawForecastStep[] {
  return Array.isArray(raw) ? (raw as RawForecastStep[]) : [];
}

/** Parse `designs.basic_stages` into drawable Directions steps. Tolerant of the
 *  sparse live shape: `status` is absent on rows written before the P/S/C chip
 *  existed (fall back to `checked`), and `substeps` is absent on leaf steps. */
export function directionSteps(raw: unknown): OracleStep[] {
  if (!Array.isArray(raw)) return [];
  return (raw as RawDirectionStep[])
    .filter((s) => s && typeof s === 'object')
    .map((s, i) => {
      const rawSubs = Array.isArray(s.substeps) ? (s.substeps as RawSubstep[]) : [];
      return {
        id: s.id || `step-${i}`,
        text: s.text ?? '',
        status: effectiveStatus({
          status: s.status === 'P' || s.status === 'S' || s.status === 'C' ? s.status : undefined,
          checked: !!s.checked,
        }),
        substeps: rawSubs
          .filter((sub) => sub && typeof sub === 'object')
          .map((sub, j) => ({ id: sub.id || `sub-${i}-${j}`, text: sub.text ?? '', checked: !!sub.checked, dueDate: sub.dueDate || null })),
        dueDate: s.dueDate || null,
      };
    });
}

/** The 5 middle steps' checked flags, padded/truncated to exactly 5. */
function middleStageFlags(steps: RawForecastStep[]): boolean[] {
  const mid = steps.filter((s) => s.type === 'step');
  const flags = mid.map((s) => !!s.checked);
  while (flags.length < 5) flags.push(false);
  return flags.slice(0, 5);
}

/** The 5 middle steps as drift inputs (dueDate/checked/override), padded to 5. */
function middleStageDriftInputs(steps: RawForecastStep[]): DriftStageInput[] {
  const mid = steps.filter((s) => s.type === 'step');
  const inputs: DriftStageInput[] = mid.map((s) => ({
    dueDate: s.date && s.date.trim() ? s.date : null,
    checked: !!s.checked,
    override: s.driftOverride ?? null,
  }));
  while (inputs.length < 5) inputs.push({ dueDate: null, checked: false, override: null });
  return inputs.slice(0, 5);
}

/** Highest-index checked + 1, 1-based; 1 if none checked — mirrors
 *  TkneTool.tsx's getCurrentMiddleStepIndex (index of last checked step). */
function currentStageOf(flags: boolean[]): number {
  for (let i = flags.length - 1; i >= 0; i--) {
    if (flags[i]) return i + 1;
  }
  return 1;
}

function toProject(row: RawDesignRow, now: Date, rules: DriftRules, groupSector: Map<string, Sector>): OracleProject | null {
  // A project with no sector of its own uses its project GROUP's sector.
  const sector = isSector(row.sector) ? row.sector : row.design_group_id ? groupSector.get(row.design_group_id) : undefined;
  if (!sector) return null;
  const steps = asForecastSteps(row.forecast_steps);
  const stages = middleStageFlags(steps);
  const cycleDays = rules[sector]?.cycleDays ?? 1;
  const gapDays = typeof row.drift_gap_days === 'number' ? row.drift_gap_days : 2;
  const drift = computeProjectDrift(middleStageDriftInputs(steps), { now, cycleDays, gapDays });
  return {
    id: row.id,
    name: row.project_title || 'Untitled project',
    sector,
    stages,
    currentStage: currentStageOf(stages),
    drift,
    steps: directionSteps(row.basic_stages),
    endDate: row.end_date || null,
    syncListId: row.sync_list_id,
    syncCategoryListId: row.sync_category_list_id,
    syncCategoryName: row.sync_category_name,
    updatedAt: row.updated_at ?? null,
  };
}

function emptyBySector(): Record<Sector, OracleProject[]> {
  return { S: [], H: [], A: [], P: [], E: [] };
}

// Demo fallback so the TKNE layer is visible before any real O.R.D.E.R
// project has been tagged with a sector. Used ONLY when the real query
// returns zero projects. `demo: true` flags that the compass is showing
// placeholder data. Delete this block once real projects exist.
function demoProject(
  id: string,
  name: string,
  sector: Sector,
  done: number,
  overdueSeg: number,
  demoSubsteps: number[] = []
): OracleProject {
  const stages = [0, 1, 2, 3, 4].map((i) => i < done);
  // Demo Directions steps: everything before `done` Completed, the one at
  // `done` Started, the rest Pending — the same read the real chip gives.
  const steps: OracleStep[] = stages.map((complete, i) => ({
    id: `demo-${id}-step-${i}`,
    text: `Step ${i + 1}`,
    status: complete ? 'C' : i === done ? 'S' : 'P',
    substeps: Array.from({ length: demoSubsteps[i] ?? 0 }, (_, j) => ({
      id: `demo-${id}-step-${i}-sub-${j}`,
      text: `Sub ${j + 1}`,
      checked: complete || (i === done && j === 0),
      dueDate: null,
    })),
    dueDate: null,
  }));
  // Demo drift: the first not-yet-done stage is `overdueSeg` segments overdue;
  // let the real cascade math carry it up so the visual is authentic.
  const inputs: DriftStageInput[] = stages.map((checked, i) => ({
    dueDate: null,
    checked,
    override: !checked && i === done ? overdueSeg : null,
  }));
  const drift = computeProjectDrift(inputs, { now: new Date(0), cycleDays: 1, gapDays: 0 });
  return {
    id: `demo-${id}`,
    name,
    sector,
    stages,
    currentStage: currentStageOf(stages),
    drift,
    steps,
    endDate: null,
    syncListId: null,
    syncCategoryListId: null,
    syncCategoryName: null,
    updatedAt: null,
  };
}
function demoBySector(): Record<Sector, OracleProject[]> {
  return {
    E: [
      demoProject('rgp', 'RGP', 'E', 4, 0, [0, 2, 0, 3, 0]),
      demoProject('invest', 'Investor deck', 'E', 2, 6, [0, 0, 3, 0, 0]),
    ],
    H: [demoProject('marathon', 'Marathon', 'H', 3, 2, [0, 0, 0, 2, 0])],
    A: [demoProject('taxes', 'Taxes', 'A', 1, 8, [0, 4, 0, 0, 0])],
    S: [demoProject('reunion', 'Reunion', 'S', 2, 0)],
    P: [demoProject('reading', 'Reading', 'P', 3, 4, [0, 0, 0, 2, 0])],
  };
}

/** Resolves a task's project (sync rule) or sector fallback chain: (1) its
 *  list's task_lists.olmps_sector, (2) group-title inference against
 *  SECTOR_LABELS, (3) null (no string). */
function linkTask(
  task: RawTaskRow,
  allProjects: OracleProject[],
  listSectorById: Map<string, Sector>,
  groups: RawGroupRow[]
): { projectId: string | null; sector: Sector | null } {
  const project = allProjects.find((d) => {
    if (d.syncListId && d.syncListId === task.list_id) return true;
    if (d.syncCategoryListId && d.syncCategoryListId === task.list_id && d.syncCategoryName && d.syncCategoryName === task.category) {
      return true;
    }
    return false;
  });
  if (project) return { projectId: project.id, sector: project.sector };

  const listSector = task.list_id ? listSectorById.get(task.list_id) : undefined;
  if (listSector) return { projectId: null, sector: listSector };

  if (task.group_id) {
    const group = groups.find((g) => g.id === task.group_id);
    if (group && group.title) {
      for (const sector of SECTORS) {
        if (nameMatchesSector(group.title, SECTOR_LABELS[sector])) {
          return { projectId: null, sector };
        }
      }
    }
  }

  return { projectId: null, sector: null };
}

export function useOracleProjects(userId: string | undefined) {
  const [bySector, setBySector] = useState<Record<Sector, OracleProject[]>>(emptyBySector);
  const [taskLinks, setTaskLinks] = useState<OracleTaskLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDemo, setIsDemo] = useState(false);

  const load = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);

    const [designsRes, tasksRes, listsRes, settingsRes] = await Promise.all([
      supabase
        .from('designs')
        .select('id, project_title, sector, is_active, forecast_steps, basic_stages, drift_gap_days, end_date, sync_list_id, sync_category_list_id, sync_category_name, updated_at, design_group_id')
        .eq('user_id', userId)
        .eq('is_active', true),
      supabase
        .from('tasks')
        .select('id, title, scheduled, done, list_id, category, group_id')
        .eq('user_id', userId)
        .not('scheduled', 'is', null),
      supabase.from('task_lists').select('id, olmps_sector').eq('user_id', userId),
      supabase.from('user_settings').select('groups, olmps_drift_rules, design_groups').eq('user_id', userId).maybeSingle(),
    ]);

    const firstError = designsRes.error || tasksRes.error || listsRes.error || settingsRes.error;
    if (firstError) {
      setError(firstError.message);
      setLoading(false);
      return;
    }

    const driftRules: DriftRules =
      settingsRes.data && typeof settingsRes.data.olmps_drift_rules === 'object' && settingsRes.data.olmps_drift_rules
        ? (settingsRes.data.olmps_drift_rules as DriftRules)
        : {};
    const now = new Date();
    const groupSector = new Map<string, Sector>();
    for (const g of Array.isArray(settingsRes.data?.design_groups) ? (settingsRes.data!.design_groups as { id?: string; sector?: string | null }[]) : []) {
      if (g?.id && isSector(g.sector ?? null)) groupSector.set(g.id, g.sector as Sector);
    }
    const grouped = emptyBySector();
    const allProjects: OracleProject[] = [];
    for (const row of (designsRes.data ?? []) as RawDesignRow[]) {
      const project = toProject(row, now, driftRules, groupSector);
      if (project) {
        grouped[project.sector].push(project);
        allProjects.push(project);
      }
    }
    // No real sector-tagged projects yet → show demo data so the TKNE layer is
    // visible. Remove demoBySector once you've tagged real projects.
    if (allProjects.length === 0) {
      setBySector(demoBySector());
      setIsDemo(true);
    } else {
      setBySector(grouped);
      setIsDemo(false);
    }

    const listSectorById = new Map<string, Sector>();
    for (const row of (listsRes.data ?? []) as RawTaskListRow[]) {
      if (isSector(row.olmps_sector)) listSectorById.set(row.id, row.olmps_sector);
    }

    const rawGroups = Array.isArray(settingsRes.data?.groups) ? (settingsRes.data!.groups as any[]) : [];
    const groups: RawGroupRow[] = rawGroups.map((g) => ({
      id: g.id,
      title: g.title ?? '',
      isActive: g.isActive !== false,
      parentId: g.parentId ?? null,
    }));

    const { start: weekStart, end: weekEnd } = currentWeekRange(new Date());
    const links: OracleTaskLink[] = [];
    for (const row of (tasksRes.data ?? []) as RawTaskRow[]) {
      const start = parseScheduled(row.scheduled);
      if (!start || start < weekStart || start >= weekEnd) continue;
      const { projectId, sector } = linkTask(row, allProjects, listSectorById, groups);
      links.push({
        taskId: row.id,
        title: row.title || 'Untitled task',
        start,
        done: !!row.done,
        projectId,
        sector,
      });
    }
    setTaskLinks(links);

    setError(null);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  return { projects: bySector, taskLinks, loading, error, isDemo, refresh: () => void load() };
}
